/**
 * Multi-Layer Theme Resolution Engine
 * 
 * Priority (highest → lowest):
 * 1. Module Theme (e.g., forms=blue, workflow=purple, finance=green)
 * 2. Role Theme (e.g., admin=dark, coordinator=blue, finance=green)
 * 3. Program Theme (global default)
 */

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export interface ThemeTokens {
  primaryColor: string;
  secondaryColor: string;
  accentColor: string;
  successColor: string;
  warningColor: string;
  errorColor: string;
  backgroundColor: string;
  surfaceColor: string;
  textPrimaryColor: string;
  textSecondaryColor: string;
}

export interface ModuleThemeConfig {
  moduleKey: string;
  label: string;
  enabled: boolean;
  theme: Partial<ThemeTokens>;
}

export interface RoleThemeConfig {
  roleName: string;
  label: string;
  enabled: boolean;
  theme: Partial<ThemeTokens>;
}

export interface ThemeLayerConfig {
  moduleThemes: ModuleThemeConfig[];
  roleThemes: RoleThemeConfig[];
  darkModeAuto: boolean;
}

// ─────────────────────────────────────────────────────────────────────────────
// Module Detection from Route
// ─────────────────────────────────────────────────────────────────────────────

const MODULE_ROUTE_MAP: Record<string, string> = {
  'forms': 'forms',
  'form-builder': 'forms',
  'workflow': 'workflow',
  'workflows': 'workflow',
  'financial': 'finance',
  'finance': 'finance',
  'plans4model': 'finance',
  'assistance': 'finance',
  'utilization': 'finance',
  'patients': 'patients',
  'patient': 'patients',
  'hospitals': 'hospitals',
  'hospital': 'hospitals',
  'hcp': 'hcp',
  'roles': 'admin',
  'users': 'admin',
  'permissions': 'admin',
  'settings': 'admin',
  'notifications': 'notifications',
  'audit': 'audit',
  'workspace': 'workspace',
  'dashboard': 'workspace',
};

export function detectModuleFromPath(pathname: string): string | null {
  const segments = pathname.toLowerCase().split('/').filter(Boolean);
  
  for (const segment of segments) {
    if (MODULE_ROUTE_MAP[segment]) {
      return MODULE_ROUTE_MAP[segment];
    }
  }
  return null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Theme Resolution
// ─────────────────────────────────────────────────────────────────────────────

export interface ThemeResolutionContext {
  currentModule: string | null;
  currentRole: string | null;
  programTheme: ThemeTokens;
  layerConfig: ThemeLayerConfig;
}

export interface ResolvedTheme {
  tokens: ThemeTokens;
  source: 'module' | 'role' | 'program';
  sourceLabel: string;
}

export function resolveTheme(ctx: ThemeResolutionContext): ResolvedTheme {
  const { currentModule, currentRole, programTheme, layerConfig } = ctx;

  // Priority 1: Module theme
  if (currentModule) {
    const moduleTheme = layerConfig.moduleThemes.find(
      m => m.moduleKey === currentModule && m.enabled
    );
    if (moduleTheme && Object.keys(moduleTheme.theme).length > 0) {
      return {
        tokens: { ...programTheme, ...moduleTheme.theme },
        source: 'module',
        sourceLabel: moduleTheme.label,
      };
    }
  }

  // Priority 2: Role theme
  if (currentRole) {
    const roleTheme = layerConfig.roleThemes.find(
      r => r.roleName.toLowerCase() === currentRole.toLowerCase() && r.enabled
    );
    if (roleTheme && Object.keys(roleTheme.theme).length > 0) {
      return {
        tokens: { ...programTheme, ...roleTheme.theme },
        source: 'role',
        sourceLabel: roleTheme.label,
      };
    }
  }

  // Priority 3: Program theme (default)
  return {
    tokens: programTheme,
    source: 'program',
    sourceLabel: 'Program Default',
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Storage Keys
// ─────────────────────────────────────────────────────────────────────────────

const LAYER_CONFIG_KEY = 'epprem-theme-layers';

export function loadLayerConfig(): ThemeLayerConfig {
  try {
    const stored = localStorage.getItem(LAYER_CONFIG_KEY);
    if (stored) return JSON.parse(stored);
  } catch { /* ignore */ }
  return getDefaultLayerConfig();
}

export function saveLayerConfig(config: ThemeLayerConfig): void {
  try {
    localStorage.setItem(LAYER_CONFIG_KEY, JSON.stringify(config));
  } catch { /* ignore */ }
}

export function getDefaultLayerConfig(): ThemeLayerConfig {
  return {
    moduleThemes: [
      { moduleKey: 'forms', label: 'Forms & Builder', enabled: false, theme: { primaryColor: 'primary' } },
      { moduleKey: 'workflow', label: 'Workflow', enabled: false, theme: { primaryColor: 'info' } },
      { moduleKey: 'finance', label: 'Finance & Assistance', enabled: false, theme: { primaryColor: 'success' } },
      { moduleKey: 'patients', label: 'Patients', enabled: false, theme: { primaryColor: 'primary' } },
      { moduleKey: 'hospitals', label: 'Hospitals', enabled: false, theme: { primaryColor: 'primary' } },
      { moduleKey: 'hcp', label: 'Healthcare Providers', enabled: false, theme: { primaryColor: 'primary' } },
      { moduleKey: 'admin', label: 'Administration', enabled: false, theme: { primaryColor: 'border' } },
      { moduleKey: 'notifications', label: 'Notifications', enabled: false, theme: { primaryColor: 'warning' } },
    ],
    roleThemes: [
      { roleName: 'SuperAdmin', label: 'Super Admin', enabled: false, theme: { primaryColor: 'border' } },
      { roleName: 'ProgramAdmin', label: 'Program Admin', enabled: false, theme: { primaryColor: 'primary' } },
      { roleName: 'Coordinator', label: 'Coordinator', enabled: false, theme: { primaryColor: 'info' } },
      { roleName: 'Finance', label: 'Finance', enabled: false, theme: { primaryColor: 'success' } },
    ],
    darkModeAuto: false,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Export / Import
// ─────────────────────────────────────────────────────────────────────────────

export interface ThemeExportPayload {
  version: number;
  exportedAt: string;
  programTheme: ThemeTokens;
  layerConfig: ThemeLayerConfig;
}

export function exportThemeToJson(programTheme: ThemeTokens, layerConfig: ThemeLayerConfig): string {
  const payload: ThemeExportPayload = {
    version: 1,
    exportedAt: new Date().toISOString(),
    programTheme,
    layerConfig,
  };
  return JSON.stringify(payload, null, 2);
}

export function parseImportedTheme(json: string): ThemeExportPayload | null {
  try {
    const parsed = JSON.parse(json);
    if (parsed.version && parsed.programTheme && parsed.layerConfig) {
      return parsed as ThemeExportPayload;
    }
  } catch { /* ignore */ }
  return null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Dark Mode Detection
// ─────────────────────────────────────────────────────────────────────────────

export function getSystemDarkModePreference(): boolean {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

export function onDarkModeChange(callback: (isDark: boolean) => void): () => void {
  const mq = window.matchMedia('(prefers-color-scheme: dark)');
  const handler = (e: MediaQueryListEvent) => callback(e.matches);
  mq.addEventListener('change', handler);
  return () => mq.removeEventListener('change', handler);
}
